import { UsageService } from '../../../core/usage/usage.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { takeUntil, tap } from 'rxjs';
import { BaseComponent } from './../../../core/components/base/base.component';
import { BillingService } from './../../../core/services/billing.service';
import { FileDownloadService } from './../../../core/services/file-download.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { GroupService } from './../../../core/services/group.service';
import { isStateModeGroup } from '../../../shared/util/state-mode.util';
import { StateCacheService } from './../../../core/services/state-cache.service';
import { ByDateBillingListComponent } from './by-date-billing-list/by-date-billing-list.component';
import { IcdSuggestionService } from './../../../core/services/icd-suggestion.service';
import {
  FLOATING_AI_DIALOG_BASE,
  ICD_REVIEW_WIZARD_PANEL_CLASSES,
} from './../../../shared/config/floating-ai-dialog.config';
import {
  FLOATING_DIALOG_LAYOUT_KEYS,
  FloatingDialogLayoutService,
} from './../../../core/services/floating-dialog-layout.service';
import {
  IcdReviewWizardComponent,
  IcdReviewWizardResult,
} from './icd-review-wizard/icd-review-wizard.component';
import { environment } from './../../../../environments/environment';
import { codingRangeStart, lastCodingRangesInfo } from './../../../core/utils/coding-ranges';
@Component({
  selector: 'app-billing-view',
  templateUrl: './billing-view.component.html',
  styleUrls: ['./billing-view.component.scss']
})
export class BillingViewComponent extends BaseComponent implements OnInit, OnDestroy {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  filterValue: string = '';
  selectedTabId: number = 0;

  exportOptions: any = {}
  exportFilterValue: string = 'month';
  /** The chip whose range is loaded; restored when the selected chip is clicked (deselected). */
  private appliedExportFilter = 'week';
  billingAlerts: any[] = [];
  @ViewChild('byDateBillingList') byDateBillingList?: ByDateBillingListComponent;
  maxDate: moment.Moment = moment();

  totalAlerts?: number;

  // Status filter properties
  private static readonly BASE_STATUS_FILTERS = [
    { value: 'inpatient', label: 'Inpatient' },
    { value: 'opd', label: 'Outpatient' }
  ];
  statusFilters: string = '';
  availableStatusFilters = BillingViewComponent.BASE_STATUS_FILTERS;

  // Master billing editor state
  showMasterBillingEditor: boolean = false;

  // Connect submission state
  isSubmitting = false;
  connectEnabled: boolean = false;

  get connectFeatureEnabled(): boolean {
    return environment.connectEnabled;
  }

  /** Connect practices also get "From Last Submitted" and the last submitted range. */
  get showSubmissionRanges(): boolean {
    return this.connectFeatureEnabled && this.connectEnabled;
  }

  get lastSubmissionDate(): string | undefined {
    return this.groupService.group?.lastBillingSubmissionCreatedDate;
  }

  // Connect submission-state filters are client-side only; never send them to the backend.
  private get backendStatusFilter(): string | undefined {
    return (this.statusFilters === 'not_submitted' || this.statusFilters === 'submitted')
      ? undefined
      : (this.statusFilters || undefined);
  }

  /** A state unit (tasks/state-mode.md) has no billing: no export, no money tooltips, no paid up front or pro bono labels. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private groupService: GroupService,
    private billingService: BillingService,
    private stateCache: StateCacheService,
    private cd: ChangeDetectorRef,
    private fileDownloadService: FileDownloadService,
    private icdSuggestionService: IcdSuggestionService,
    private floatingLayout: FloatingDialogLayoutService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.groupService.selectedGroup$.pipe(
      tap(selectedGroup => {
        if (selectedGroup) {
          const wasConnectEnabled = this.connectEnabled;
          this.connectEnabled = !!(selectedGroup?.connectEnabled && selectedGroup?.connectTenantId);
          if (wasConnectEnabled && !this.connectEnabled && this.selectedTabId === 2) {
            this.selectedTabId = 0;
            this.stateCache.set('codingTab', 0);
          }
          if (this.connectFeatureEnabled && this.connectEnabled) {
            this.availableStatusFilters = [
              ...BillingViewComponent.BASE_STATUS_FILTERS,
              { value: 'not_submitted', label: 'Not Submitted to Connect' },
              { value: 'submitted', label: 'Submitted to Connect' }
            ];
          } else {
            this.availableStatusFilters = BillingViewComponent.BASE_STATUS_FILTERS;
            if (this.statusFilters === 'not_submitted' || this.statusFilters === 'submitted') {
              this.statusFilters = '';
            }
          }
          let cached = this.stateCache.get<string>('exportFilterValue');
          if (cached === 'submitted' && !this.showSubmissionRanges) {
            cached = 'week';
          }
          if (!cached) {
            this.setExportOptions(selectedGroup);
            this.loadBilling();
          } else {
            this.exportFilterValue = cached;
            setTimeout(() => {
              this.onExportFilterSelection(this.exportFilterValue);
            });
          }

        }
      })
    ).subscribe();

    this.billingService.billingAlerts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(alerts => {
        this.billingAlerts = alerts;
        this.totalAlerts = alerts?.length || 0;
      }, error => {
        console.error('Error fetching alerts:', error);
      });
  }

  ngOnInit(): void {
    const currentTab = this.stateCache.get<number>('codingTab')! || 0;
    this.selectedTabId = currentTab;

  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stateCache.set('codingTab', this.selectedTabId);
    this.stateCache.set('exportFilterValue', this.exportFilterValue);
  }

  onTabChange(index: number) {
    this.selectedTabId = index;
    this.stateCache.set('codingTab', index);
  }

  private setExportOptions(selectedGroup: any) {
    const { lastBillingExportCreatedDate, lastBillingExportStartDate, lastBillingExportEndDate } = selectedGroup;
    const defaultEndDate = moment().utc();
    // Default range is the last week ('week'). It used to be 'last' (since the last export),
    // which for a group that hadn't exported in months loaded every page of up to 16 months of
    // codes on each visit, and that query was the biggest load on the production database
    // (tasks/azure-sql-cpu.md §3). 'Since last export' is still one click away.
    this.exportFilterValue = 'week';
    this.appliedExportFilter = 'week';
    let startDate = moment.utc().subtract(7, 'days');
    let endDate = defaultEndDate;

    // Ensure startDate is not greater than endDate
    while (startDate.isAfter(endDate, 'day')) {
      startDate = endDate.clone().subtract(1, 'day');
    }

    // Ensure dates are not in the future
    const today = moment.utc();
    if (startDate.isAfter(today, 'day')) {
      startDate = today;
    }
    if (endDate.isAfter(today, 'day')) {
      endDate = today;
    }

    // Ensure proper date range formatting with startOf('day') and endOf('day')
    this.exportOptions = {
      startDate: startDate.startOf('day'),
      endDate: endDate.endOf('day'),
      lastBillingExportCreatedDate,
      lastBillingExportStartDate,
      lastBillingExportEndDate
    };
  }

  /**
   * @param onLoaded ⚠ Fired once the reload has actually landed.
   *
   * `resetLoadParameters` EMPTIES the list before the reload starts, so between calling this and
   * the response arriving `byDateBillingList.filteredPatients` is `[]` — not stale, empty. Any
   * work that reads the patient set has to wait for this callback rather than assume the list
   * survives the call. Resuming the export from the review wizard did assume that, and produced
   * an export document with no patients in it.
   *
   * Fires on error as well as success: a refresh that failed must not strand a caller waiting
   * for a callback that never comes. Guarded so it fires at most once, because a stream that
   * emits twice would otherwise run the caller's work twice — and here that meant two exports.
   */
  loadBilling(onLoaded?: () => void) {
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      onLoaded?.();
    };

    this.billingService.resetLoadParameters(this.groupService.group)
    this.billingService.loadGroupBilling(this.exportOptions.startDate, this.exportOptions.endDate, this.backendStatusFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => fire(), error: () => fire() });
  }

  hasAlerts(): boolean {
    if (!this.billingAlerts) {
      return false;
    }

    return this.billingAlerts.filter(alert => {
      const alertDate = moment(alert.date);
      return alertDate.isBetween(this.exportOptions.startDate, this.exportOptions.endDate, undefined, '[]');
    }).length > 0;
  }


  applyFilter(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value.toLowerCase();
  }

  lastRangesInformation(): string {
    return lastCodingRangesInfo(this.groupService.group, this.showSubmissionRanges);
  }


  /**
   * @param value the chip's value. The template passes `$event.value`, and this used to read
   * `event.value` off that STRING (undefined), so every chip fell through to `default`: the
   * first click moved the range to a month and later clicks changed nothing (found 2026-09-23).
   */
  onExportFilterSelection(value: string | null | undefined) {
    if (!value) {
      // Clicking the selected chip deselects it. Keep the current range and select it again.
      setTimeout(() => this.exportFilterValue = this.appliedExportFilter);
      return;
    }
    this.appliedExportFilter = value;

    const group = this.groupService.group;

    const { lastBillingExportCreatedDate, lastBillingExportStartDate, lastBillingExportEndDate } = group;

    const startDate = codingRangeStart(value, group);
    const endDate = moment();

    // Ensure proper date range formatting with startOf('day') and endOf('day')
    this.exportOptions = {
      startDate: startDate.startOf('day'),
      endDate: endDate.endOf('day'),
      lastBillingExportCreatedDate,
      lastBillingExportStartDate,
      lastBillingExportEndDate
    };
    this.loadBilling();
  }

  /** Every patient the current export would actually cover — the same set both actions bill. */
  private exportPatientIds(): string[] {
    const groups = this.byDateBillingList?.filteredPatients || [];
    return [...new Set(
      groups.flatMap((group: any) => (group.patients || []).map((p: any) => p.id)).filter(Boolean),
    )] as string[];
  }

  /**
   * Stops an export that would bill coding nobody has agreed to.
   *
   * ICD-10 decides what a claim pays. Exporting while proposals sit unreviewed means submitting a
   * coding set the practice has not signed off — and unlike a procedure code, a wrong diagnosis is
   * not obvious on the invoice, it comes back as a rejection weeks later.
   *
   * Three outcomes, deliberately, because two would force a false choice:
   *   - **Review now**   → open the wizard, CANCEL this export. Re-triggered afterwards, or from
   *                        the wizard's last step.
   *   - **Export anyway** → proceed. There are legitimate reasons: a deadline, or a queue the
   *                        biller has already decided about.
   *   - **Cancel**       → do nothing at all.
   *
   * Silent when the group has not opted in (§10) or when nothing is pending — the common case,
   * and the one that must not cost a click.
   *
   * @returns true when the caller should continue with the export.
   */
  /**
   * Patients the user was shown in the review wizard, so the export resumed from it does not
   * re-ask about the very patients it just walked them through. One-shot: see where it is read.
   */
  private icdResumeAcknowledged: Set<string> | null = null;

  private async confirmIcdReview(label: string, rerun: () => void): Promise<boolean> {
    const group = this.groupService.group;
    // The server answers `enabled: false` for a group that has not opted in, but asking at all
    // costs a round trip on every export for practices that will never use this.
    if (!group?.id || group?.icdSuggestionsEnabled !== true) return true;

    const patientIds = this.exportPatientIds();
    if (!patientIds.length) return true;

    let pending: any[] = [];
    try {
      const response: any = await this.icdSuggestionService
        .getPendingSummary(group.id, patientIds)
        .toPromise();
      pending = response?.data || [];
    } catch (error) {
      // ⚠ NEVER BLOCK THE EXPORT ON THIS CHECK FAILING. It is an advisory guard; a coding queue
      // the server could not report on must not stop a practice billing.
      console.error('Could not check ICD suggestions before export:', error);
      return true;
    }

    if (!pending.length) return true;

    // ── Resuming from the wizard ────────────────────────────────────────────────
    //
    // ⚠ ONE-SHOT, AND CONSUMED HERE. Set when the wizard opens, cleared on the next check —
    // so it only ever suppresses the prompt for the run that immediately follows a review, and
    // a later export in the same session is challenged normally.
    //
    // Why not simply skip the check on resume: this panel is modeless, so the date range can be
    // changed while it is open, and a blanket skip would export a set nobody reviewed. Why not
    // re-prompt unconditionally, which is what it did: walking the wizard with Next does not
    // RESOLVE anything, so the queue is still pending at the end and the user was asked the same
    // question again immediately after answering it — with a button that had just promised to
    // export. Comparing against what they were actually shown gets both: silent when they have
    // seen these patients, and a fresh prompt the moment the export covers someone they have not.
    const acknowledged = this.icdResumeAcknowledged;
    this.icdResumeAcknowledged = null;
    const unseen = acknowledged ? pending.filter((p) => !acknowledged.has(String(p.patientId))) : pending;
    if (!unseen.length) return true;
    pending = unseen;

    const totalSuggestions = pending.reduce((sum, p) => sum + Number(p?.counts?.total || 0), 0);
    const proceed = await this.showConfirmationDialog(
      'Unreviewed ICD-10 Suggestions',
      `${pending.length} patient(s) in this ${label} have ${totalSuggestions} coding suggestion(s) `
      + 'that have not been reviewed. Do you want to review them now? '
      + 'We strongly suggest you do this since ICD-10 coding can affect claim outcomes.',
      [],
      'Export anyway',
      'Review now',
      false,
    );

    // "Export anyway" is the CANCEL button here: the confirm action is the safe one, so the
    // default keystroke and the prominent button both lead to reviewing rather than billing.
    if (!proceed) return true;

    // What they are about to be shown. If they come back out via "Done — export now", these are
    // the patients they have already been warned about, so the resumed export does not ask again.
    this.icdResumeAcknowledged = new Set(pending.map((p) => String(p.patientId)));

    this.openIcdReviewWizard(pending, label, rerun);
    return false;
  }

  /** Opens the modeless, draggable review wizard over the billing list. */
  private openIcdReviewWizard(
    patients: any[],
    label: string,
    rerun: () => void,
  ): void {
    const sizePos = this.floatingLayout.getMatDialogOpenOptions(
      FLOATING_DIALOG_LAYOUT_KEYS.icdReviewWizard,
      { width: '900px', height: '80vh' },
    );

    const ref = this.dialog.open(IcdReviewWizardComponent, {
      ...FLOATING_AI_DIALOG_BASE,
      ...sizePos,
      disableClose: true,
      panelClass: ICD_REVIEW_WIZARD_PANEL_CLASSES,
      data: { patients, exportLabel: label },
    });

    ref.afterClosed().subscribe((result: IcdReviewWizardResult | undefined) => {
      // Reloaded below when the export is being resumed, so the two cannot race.
      if (result?.changed && !result?.runExport) this.loadBilling();

      // ── Resuming the interrupted export ─────────────────────────────────────────
      //
      // `rerun` is a closure the CALLER built, so it carries what this wizard cannot know: which
      // of the two actions was interrupted, and for a document export, whether it was docx or
      // xlsx. It does NOT capture the date range or the status filter — those are read from
      // `exportOptions` when it runs, so the export matches what the screen currently shows
      // rather than a remembered range the user can no longer see.
      //
      // ⚠ THE RESUMED RUN GOES BACK THROUGH `confirmIcdReview`, DELIBERATELY. It is tempting to
      // skip the check on the way out of the review — they just reviewed, after all — but this
      // panel is MODELESS, so the billing list underneath stays interactive: the date range can
      // be changed while the wizard is open. Skipping the check would then export a range whose
      // patients were never reviewed, silently, which is the exact thing the guard exists to
      // prevent. Re-checking costs one request and is silent when the queue is genuinely clear.
      //
      // ⚠ SEQUENCED AFTER THE RELOAD, NOT ALONGSIDE IT.
      //
      // `loadBilling` empties the list before it refills it. Firing the export on a timer next to
      // it read `filteredPatients` mid-reset, sent an EMPTY patient list, and produced an export
      // document with nothing in it — which looks like the export is broken rather than like a
      // race. The extra tick lets the child list recompute from the new data before the export
      // reads it.
      if (!result?.runExport) return;
      const resume = () => setTimeout(() => rerun(), 0);
      if (result?.changed) this.loadBilling(resume); else resume();
    });
  }

  async exportBilling(format: 'docx' | 'xlsx' = 'docx') {

    if (!await this.confirmIcdReview('export', () => this.exportBilling(format))) return;

    if (this.hasAlerts()) {
      const confirmed = await this.showConfirmationDialog('Alerts Found', 'There are pending coding alerts for this export date range. Are you sure you want to continue?');
      if (!confirmed) return;
    }

    const filteredPatients = this.byDateBillingList?.filteredPatients;
    const patientIds = (filteredPatients || [])
      .flatMap((group: any) => (group.patients || []).map((patient: any) => patient.id))
      .filter(Boolean);

    // ⚠ AN EMPTY SET IS A FAULT, NOT AN EXPORT. Without this the server was handed no patients
    // and cheerfully produced an empty document — which reads as "the export is broken" and
    // gives no clue that the list simply had not finished loading. `submitBillingToSwitching`
    // has always guarded this; the export path never did, and that is how a mid-reload race
    // surfaced as a downloaded file with nothing in it.
    if (!patientIds.length) {
      this.showToastMessage('No patients in the selected range');
      return;
    }

    this.billingService.exportBilling(this.groupService.group.id, this.exportOptions.startDate, this.exportOptions.endDate, patientIds, this.backendStatusFilter, format)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        async (blob: Blob) => {
          const fileExtension = format === 'xlsx' ? 'xlsx' : 'docx';
          await this.fileDownloadService.download(blob, `medicai_coding_export_${moment().valueOf()}.${fileExtension}`);
          this.showToastMessage('Coding exported successfully');
        },
        (error) => {
          console.error('Error exporting coding:', error);
          this.showToastMessage('There was an error exporting your coding. Please try again.');
        });
  }

  async submitBillingToSwitching(): Promise<void> {
    if (!await this.confirmIcdReview('submission', () => this.submitBillingToSwitching())) return;

    if (this.hasAlerts()) {
      const confirmed = await this.showConfirmationDialog('Alerts Found', 'There are pending coding alerts for this range. Submit anyway?');
      if (!confirmed) return;
    }
    const group = this.groupService.group;
    if (!group?.connectEnabled || !group?.connectTenantId) {
      this.showToastMessage('Group is not linked to Connect');
      return;
    }
    const filteredPatients = this.byDateBillingList?.filteredPatients;
    if (!filteredPatients?.length) {
      this.showToastMessage('No patients in the selected range');
      return;
    }
    const patientIds = filteredPatients.flatMap(g => g.patients.map((p: any) => p.id));
    console.debug('submitBillingToSwitching: submitting', { groupId: group.id, startDate: this.exportOptions.startDate?.toISOString?.(), endDate: this.exportOptions.endDate?.toISOString?.(), patientCount: patientIds.length });
    this.isSubmitting = true;
    // The service turns the picked calendar days into the stored UTC boundaries
    // (billingRangeStartIso). Converting here as well moved the start back a day.
    this.usage.flow('claim submission', 'start');
    this.billingService.submitBillingToSwitching(group.id, this.exportOptions.startDate, this.exportOptions.endDate, patientIds, this.backendStatusFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.cd.detectChanges();
          if (res.success) {
            if (res.lastBillingSubmission) {
              const { createdDate, startDate, endDate } = res.lastBillingSubmission;
              Object.assign(group, {
                lastBillingSubmissionCreatedDate: createdDate,
                lastBillingSubmissionStartDate: startDate,
                lastBillingSubmissionEndDate: endDate,
              });
            }
            this.showToastMessage('Billing submitted to Connect');
            this.usage.flow('claim submission', 'done');
          } else {
            this.showToastMessage('Failed to submit billing to Connect');
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.cd.detectChanges();
          const message = err?.error?.message;
          if (err?.status === 409) {
            this.showToastMessage(message || 'A submission for this range is already pending.');
          } else {
            this.showToastMessage(message || 'Error submitting billing to Connect');
          }
        }
      });
  }

}
